/*
  The public verification report for one certification number, laid out as the office
  portal's "Card Grading #<cert>" window (docs/specs/pop-grade-report.md) and showing only
  what a customer needs.

  **What it shows (D3):** cert number, slab type, the grade tile and four sub-grade arcs,
  game / set / card name / number / finish / rarity / language / release year, the
  catalog's front and back art, and a read-only label preview.

  **What it never shows (D4):** the public gradings feed also carries `office`,
  `cardValue`, `bucket`, `publicLabel`, `stageKey`, `completedOn`, `notes` and `files`. The
  portal's Certification, Details and Logs tabs (card order number, card entry, PS slim
  sticker, NFC controls, errors, value, activity) have no counterpart here. Every row comes
  from lib/gradeReport's selectors, so a field appears only by being added there on purpose,
  and GradeReportView.test.tsx fails if one of the hidden values reaches the DOM.

  **One screen from lg up (D2).** The site header and the cert-lookup band take the top of
  the window; the sheet below them is two columns — Card Info and the label on the left,
  the grade and the card on the right — and the card images take their height from the
  viewport (CardFaces), so the whole report fits a 1440×900 or 1920×1080 window without
  scrolling. Below lg it stacks, grade first, and scrolls like any page.

  **Images are the CATALOG's art**, not photographs of this slab — the graded card's own
  `files` come back empty on the public API — and the panel says so (D6).

  **A cert still being graded (D7)** shows its card details and "Grading in progress" in
  place of the grade, and no label: no stage name, office or date.

  Loading, error and not-found stay three states, and the NFC verdict band is captured from
  router state exactly once, as before (D10). The `<main>` and the content width come from
  the shell; this view adds neither.
*/

import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { VerdictBand } from 'components/VerdictBand';
import { readVerdict } from 'lib/nfcVerdict';
import {
  isGradeReady,
  ReportRow,
  selectCardInfoRows,
  slabTypeLabel,
} from 'lib/gradeReport';

import CardFaces from 'components/report/CardFaces';
import CardInfoPanel from 'components/report/CardInfoPanel';
import GradeTile from 'components/report/GradeTile';
import LabelPreview from 'components/report/LabelPreview';

import { DataTableEmpty, DataTableError } from 'components/DataTable';
import { useGetGamesQuery } from 'services/pcgPublicV2';

import { useGetGradeReportViewQuery } from 'services/pcgPublic';

import slabStandard from 'images/slabs/slab-standard.png';
import slabPlayer from 'images/slabs/slab-player.png';
import slabSlim from 'images/slabs/slab-slim.png';

/* The loading, error and not-found states keep the plain panel. */
const PANEL = 'skin-surface rounded-2xl border border-pcg-border bg-pcg-panel p-4 sm:p-6';

/* The report itself: the portal window's gold-framed sheet. */
const SHEET = 'skin-surface overflow-hidden rounded-[25px] border-2 border-pcg-goldDark bg-pcg-panel';

/* The grade and image panels: the portal's 2px gold, 10px-radius frames. */
const GOLD_PANEL = 'rounded-[10px] border-2 border-pcg-gold p-3';

/* Roughly a line of text, in the one colour that is subtle in both themes. */
const SKELETON_BAR = 'rounded bg-pcg-raised';

/* The portal's slab icons: Standard, Player Slab (Metal / Thick) and Slim. */
const slabIconFor = (slab: string | null | undefined): string => {
  const text = (slab || '').toLowerCase();
  if (text.includes('slim')) {
    return slabSlim;
  }
  if (text.includes('player') || text.includes('metal') || text.includes('thick')) {
    return slabPlayer;
  }
  return slabStandard;
};

function GradeReportView() {
  const params = useParams();
  const { gradedId = '' } = params;

  /*
    The NFC verdict (P4: D14, D14b, N3). It arrives ONLY as router state from the /t tap
    route — never a path segment, a query parameter or storage — and a report reached
    without a tap shows nothing about verification at all (R9/D14).

    Captured once, into state, on the first render; then the history entry is replaced
    with the same URL and no state. BrowserRouter keeps location.state in
    window.history.state.usr, which the browser restores on reload and back/forward, so
    without the replace a refresh would show the band again. The captured value survives
    the replace's re-render, so the band shows exactly once. Nothing else here reads the
    location.
  */
  const location = useLocation();
  const navigate = useNavigate();
  const [verdict] = useState(() => readVerdict(location.state));
  useEffect(() => {
    if (location.state != null) {
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
    // Once, on mount: the capture above is a one-shot by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, cardInfo, error, isFetching } = useGetGradeReportViewQuery({ gradedId });
  /*
    A grading's `game` is the CODE — the page read "Card Game: pkm" against a customer's
    certificate. The catalog is the only place the display name lives, so it is resolved
    here and the code kept as the fallback: the games call failing must not blank out a row
    of the cert, and an unknown code is still more use than nothing.
  */
  const { data: games } = useGetGamesQuery();

  if (isFetching) {
    return (
      <>
        <Helmet title={gradedId || ''} />
        <div className={PANEL}>
          <p role="status" className="sr-only">
            Loading report
          </p>
          {/* The skeleton keeps the report's shape so the panel does not jump. */}
          <div aria-hidden="true" className="flex flex-col items-center gap-4 py-6">
            <div className={`${SKELETON_BAR} h-5 w-2/3 max-w-sm`} />
            <div className={`${SKELETON_BAR} h-9 w-1/3 max-w-[12rem]`} />
            <div className={`${SKELETON_BAR} mt-4 h-40 w-full max-w-[800px]`} />
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet title={gradedId || ''} />
        {/*
          R8: the verdict was already cleared from history, so a report-load error must not
          discard it — the band is still true about the chip.
        */}
        {verdict && (
          <div className="mb-4">
            <VerdictBand verdict={verdict} />
          </div>
        )}
        <div className={PANEL}>
          <DataTableError
            title="Report unavailable"
            description="This report could not be loaded. Please try again."
          />
        </div>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <Helmet title={gradedId || ''} />
        <div className={PANEL}>
          <DataTableEmpty
            title="Report does not exist."
            description={`No certification matches #${gradedId}.`}
          />
        </div>
      </>
    );
  }

  const row = data as ReportRow;
  const gameCode = cardInfo?.gameCode || row.game;
  const gameLabel =
    games?.find((candidate) => candidate.gameCode === gameCode)?.name || row.game;

  const cardName = cardInfo?.cardName || row.cardName || '';
  const slabLabel = slabTypeLabel(row.slab);
  const ready = isGradeReady(row);
  const infoRows = selectCardInfoRows(row, gameLabel);

  /* Both faces always render, as the portal's do: a missing one shows a placeholder (D6). */
  const faces = [
    { label: 'Front', src: cardInfo?.image || null },
    { label: 'Back', src: cardInfo?.reverseImage || null },
  ];

  return (
    <>
      <Helmet title={gradedId || ''} />

      {verdict && (
        <div className="mb-4">
          <VerdictBand verdict={verdict} />
        </div>
      )}

      {/*
        The report reads as the office portal's Card Grading window (D1): a gold-framed
        sheet with a header bar, the card's details and label on the left, the grade and the
        card itself on the right. From lg it is sized to fit one screen (D2).
      */}
      <article aria-labelledby="cert-heading" className={SHEET}>
        <header className="flex items-center gap-3 border-b border-pcg-goldDark/30 px-4 py-2.5 sm:px-5">
          <span
            aria-hidden="true"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
            style={{ background: 'linear-gradient(to bottom, rgb(var(--pcg-gold)), rgb(var(--pcg-gold-deep)))' }}
          >
            <img src={slabIconFor(row.slab)} alt="" className="h-6 w-auto" />
          </span>
          <div className="min-w-0">
            <h1 id="cert-heading" className="m-0 font-display text-lg font-bold text-pcg-text sm:text-xl">
              {/* The number never splits: a phone wraps between the words and the number. */}
              Card Grading <span className="whitespace-nowrap">#{gradedId}</span>
            </h1>
            {slabLabel && (
              <p className="m-0 text-[15px] font-medium text-pcg-textMuted">Slab Type: {slabLabel}</p>
            )}
          </div>
        </header>

        <div className="grid grid-cols-1 gap-3 p-3 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          {/* Grade first in reading order on a phone: it is why the page was opened. */}
          <div className="flex flex-col gap-3 lg:order-2">
            <section aria-labelledby="cert-grade-heading" className={GOLD_PANEL}>
              <h2 id="cert-grade-heading" className="sr-only">
                Grade
              </h2>
              {ready ? (
                <GradeTile row={row} />
              ) : (
                <p className="m-0 py-6 text-center font-display text-xl font-semibold text-pcg-text">
                  Grading in progress
                </p>
              )}
            </section>

            <section aria-labelledby="cert-card-heading" className={GOLD_PANEL}>
              <h2 id="cert-card-heading" className="sr-only">
                The card
              </h2>
              <CardFaces cardName={cardName} faces={faces} />
            </section>
          </div>

          <div className="flex flex-col gap-3 lg:order-1">
            <CardInfoPanel rows={infoRows} row={row} headingId="cert-details-heading" />
            {ready && <LabelPreview row={row} gameLabel={gameLabel} headingId="cert-label-heading" />}
          </div>
        </div>
      </article>
    </>
  );
}

export default GradeReportView;
